import { Checkbox } from '@telegram-apps/telegram-ui';
import {
  Badge,
  Button,
  Card,
  Caption,
  Image,
  Spinner,
} from '@telegram-apps/telegram-ui';
import type { Product, ProductStatus } from '../types/product';
import { formatImageUrl } from '../lib/imageUrl';
import { PRODUCT_IMAGE_ASPECT_RATIO, PRODUCT_IMAGE_BACKGROUND, PRODUCT_IMAGE_OBJECT_FIT } from '../lib/imagePolicy';

const STATUS_META: Record<
  ProductStatus,
  { mode: 'primary' | 'critical' | 'secondary' | 'gray' | 'white'; label: string }
> = {
  draft: { mode: 'gray', label: 'Черн.' },
  active: { mode: 'primary', label: 'Актив.' },
  archived: { mode: 'secondary', label: 'Арх.' },
};

function previewSrc(p: Product): string | null {
  const u = p.preprocessedImageUrl ?? p.sourceImageUrl;
  if (!u || !/^https?:\/\//i.test(u)) return null;
  return formatImageUrl(u);
}

export function ProductCard({
  product,
  selected,
  bulkMode,
  onOpen,
  onSendClient,
  onToggleSelect,
}: {
  product: Product;
  selected: boolean;
  bulkMode: boolean;
  onOpen: () => void;
  onSendClient: () => void;
  onToggleSelect: () => void;
}) {
  const src = previewSrc(product);
  const meta = STATUS_META[product.status];
  const skuLine = product.sku ?? '—';
  const pendingSoft = product.preprocessingStatus === 'pending';
  const busyProcess = product.preprocessingStatus === 'processing';

  const handleBodyClick = () => {
    if (bulkMode) onToggleSelect();
    else onOpen();
  };

  return (
    <Card style={{ position: 'relative', overflow: 'hidden' }}>
      {(bulkMode || selected) && (
        <div
          style={{
            position: 'absolute',
            top: 4,
            left: 4,
            zIndex: 5,
            background: 'var(--tgui--bg_color)',
            borderRadius: 8,
            padding: 2,
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={selected}
            onChange={() => onToggleSelect()}
            aria-label="Выбрать товар в списке"
          />
        </div>
      )}
      <div style={{ cursor: 'pointer' }} onClick={() => handleBodyClick()}>
        <div style={{ aspectRatio: PRODUCT_IMAGE_ASPECT_RATIO, background: PRODUCT_IMAGE_BACKGROUND, position: 'relative' }}>
          {pendingSoft && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--tgui--bg_color, rgba(255,255,255,0.45))',
                color: 'var(--tgui--text_color, #000)',
                fontSize: 13,
                fontWeight: 600,
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              Готовим фото для AI
            </div>
          )}
          {src ? (
            <Image
              src={src}
              alt={product.name}
              decoding="async"
              style={{ width: '100%', height: '100%', objectFit: PRODUCT_IMAGE_OBJECT_FIT, display: 'block', background: PRODUCT_IMAGE_BACKGROUND }}
              fallbackIcon={<Caption level="2">нет фото</Caption>}
            />
          ) : (
            <Caption level="2" style={{ padding: 12 }}>
              нет превью
            </Caption>
          )}
          {busyProcess && (
            <div
              style={{
                position: 'absolute',
                right: 6,
                bottom: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 8px',
                borderRadius: 8,
                background: 'rgba(0,0,0,0.5)',
                pointerEvents: 'none',
                zIndex: 3,
              }}
            >
              <Spinner size="s" />
              <Caption level="2" style={{ color: '#fff' }}>
                Готовим
              </Caption>
            </div>
          )}
        </div>
        <div style={{ padding: '8px 10px', minHeight: 72 }}>
          <div
            style={{
              fontWeight: 600,
              lineHeight: 1.25,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              marginBottom: 4,
            }}
          >
            {product.name}
          </div>
          <Caption level="2" style={{ marginBottom: 6 }}>
            SKU: {skuLine}
          </Caption>
          <Badge type="number" mode={meta.mode}>
            {meta.label}
          </Badge>
          {product.status === 'active' && !bulkMode && (
            <div style={{ marginTop: 8 }}>
              <Button
                mode="outline"
                size="s"
                stretched
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onSendClient();
                }}
              >
                Отправить клиенту
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
